// src/navigator.ts
import { batch as batch2 } from "solid-js";

// src/Router.tsx
import {
  batch,
  children,
  createContext,
  createMemo as createMemo2,
  createSignal,
  onCleanup,
  useContext
} from "solid-js";
import { delegateEvents } from "solid-js/web";
import { createStore as createStore2, reconcile as reconcile2 } from "solid-js/store";

// src/utils/matcher.ts
function createMatcher(path, partial, matchFilters) {
  const [pattern, splat] = path.split("/*", 2);
  const segments = pattern.split("/").filter(Boolean);
  const len = segments.length;
  return (location2) => {
    const locSegments = location2.split("/").filter(Boolean);
    const lenDiff = locSegments.length - len;
    if (lenDiff < 0 || lenDiff > 0 && splat === void 0 && !partial) {
      return null;
    }
    const match = {
      path: len ? "" : "/",
      params: {}
    };
    const matchFilter = (s) => matchFilters === void 0 ? void 0 : matchFilters[s];
    for (let i = 0; i < len; i++) {
      const segment = segments[i];
      const locSegment = locSegments[i];
      const dynamic = segment[0] === ":";
      const key = dynamic ? segment.slice(1) : segment;
      if (dynamic && matchSegment(locSegment, matchFilter(key))) {
        match.params[key] = locSegment;
      } else if (dynamic || !matchSegment(locSegment, segment)) {
        return null;
      }
      match.path += `/${locSegment}`;
    }
    if (splat) {
      const remainder = lenDiff ? locSegments.slice(-lenDiff).join("/") : "";
      if (matchSegment(remainder, matchFilter(splat))) {
        match.params[splat] = remainder;
      } else {
        return null;
      }
    }
    return match;
  };
}
function matchSegment(input, filter) {
  const isEqual = (s) => s.localeCompare(input, void 0, { sensitivity: "base" }) === 0;
  if (filter === void 0) {
    return true;
  } else if (typeof filter === "string") {
    return isEqual(filter);
  } else if (typeof filter === "function") {
    return filter(input);
  } else if (Array.isArray(filter)) {
    return filter.some(isEqual);
  } else if (filter instanceof RegExp) {
    return filter.test(input);
  }
  return false;
}

// src/createLocation.ts
import { createEffect, createMemo, on } from "solid-js";
import { reconcile } from "solid-js/store";
var createLocation = (path, query, setQuery) => {
  const url = createMemo(() => {
    return new URL(path(), "http://owo");
  });
  createEffect(
    on(url, () => {
      const newQuery = {};
      url().searchParams.forEach((value, key) => {
        newQuery[key] = value;
      });
      setQuery(reconcile(newQuery));
    })
  );
  const search = createMemo(() => url().search);
  const pathname = createMemo(() => url().pathname);
  const hash = createMemo(() => url().hash);
  const state = createMemo(on([hash, pathname, search], () => history.state));
  return {
    query,
    get search() {
      return search();
    },
    get pathname() {
      return pathname();
    },
    get hash() {
      return hash();
    },
    get state() {
      return state();
    }
  };
};

// src/utils/utils.ts
var isValidPath = (routes, pathname) => {
  return routes().find((route) => {
    const matcher = createMatcher(route.path);
    return matcher(pathname);
  });
};
var getSearchAndHash = () => location.search + location.hash;
function expandOptionals(pattern) {
  let match = /(\/?\:[^\/]+)\?/.exec(pattern);
  if (!match)
    return [pattern];
  let prefix = pattern.slice(0, match.index);
  let suffix = pattern.slice(match.index + match[0].length);
  const prefixes = [prefix, prefix += match[1]];
  while (match = /^(\/\:[^\/]+)\?/.exec(suffix)) {
    prefixes.push(prefix += match[1]);
    suffix = suffix.slice(match[0].length);
  }
  return expandOptionals(suffix).reduce(
    (results, expansion) => [...results, ...prefixes.map((p) => p + expansion)],
    []
  );
}

// src/Router.tsx
var RouterContext = createContext();
function Router(props) {
  if (!props.children) {
    throw new Error("Router: No children provided.");
  }
  const childRoutes = children(() => props.children).toArray;
  const routes = createMemo2(() => expandOptionalRoutes(flattenedRoutes(childRoutes())));
  const [pathname, setPathname] = createSignal(location.pathname);
  const [searchAndHash, setSearchAndHash] = createSignal(getSearchAndHash());
  const [params, setParams] = createStore2({});
  const [query, setQuery] = createStore2({});
  const pathnameWithSearchAndHash = createMemo2(() => pathname() + searchAndHash());
  const loc = createLocation(pathnameWithSearchAndHash, query, setQuery);
  const matched = createMemo2(() => {
    if (!routes())
      return;
    let pathMatch = null;
    let matchedRoute;
    for (const route of routes()) {
      const matcher = createMatcher(route.path);
      const match = matcher(pathname());
      if (match) {
        pathMatch = match;
        matchedRoute = route;
        break;
      }
    }
    setParams(reconcile2(pathMatch?.params || {}));
    if (!matchedRoute || !pathMatch) {
      return void 0;
    }
    return { match: pathMatch, route: matchedRoute };
  });
  const navigate = createNavigate(routes, pathname, setPathname, setSearchAndHash);
  const onPopState = (_event) => {
    batch(() => {
      setPathname(location.pathname);
      setSearchAndHash(getSearchAndHash());
    });
  };
  const onClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)
      return;
    const target = event.composedPath().find((el) => el instanceof Node && el.nodeName.toUpperCase() === "A");
    if (target?.tagName !== "A")
      return;
    if (!target.hasAttribute("sn-link"))
      return;
    const href = target.getAttribute("href") || "";
    if (href.startsWith("http://") || href.startsWith("https://"))
      return;
    event.preventDefault();
    navigate(href, {
      replace: target.hasAttribute("replace"),
      state: JSON.parse(target.getAttribute("d-state") || "null")
    });
  };
  delegateEvents(["click"]);
  window.addEventListener("popstate", onPopState);
  document.addEventListener("click", onClick);
  onCleanup(() => {
    window.removeEventListener("popstate", onPopState);
    document.removeEventListener("click", onClick);
  });
  return <RouterContext.Provider
    value={{
      routes,
      matched,
      navigate,
      params,
      location: loc,
      setSearchAndHash,
      setPathname,
      setQuery,
      query
    }}
  >{props.root?.()}</RouterContext.Provider>;
}
var useRouterContext = () => {
  const context = useContext(RouterContext);
  if (!context) {
    throw new Error("Router: cannot find a RouterContext");
  }
  return context;
};
function useParams() {
  const context = useRouterContext();
  return context.params;
}
var useLocation = () => useRouterContext().location;
var flattenedRoutes = (routes) => {
  return routes.map((route) => flattenedRoute(route)).flat();
};
var flattenedRoute = (route) => {
  const routes = [];
  const components = route.components || {};
  let lastComponent = void 0;
  if (route.component) {
    lastComponent = route.component;
  }
  routes.push({
    ...route,
    components: { ...components },
    mergedComponents: components,
    component: route.component || lastComponent
  });
  if (!route.children)
    return routes;
  for (let i = 0; i < route.children.length; i++) {
    const child = route.children[i];
    if (!child)
      continue;
    if (child.components) {
      Object.assign(components, child.components);
    }
    if (child.component) {
      lastComponent = child.component;
    }
    if (!child.path.startsWith("/")) {
      if (!child.component) {
        throw new Error("Route: No component for " + child.path);
      }
      components[child.path] = child.component;
      continue;
    }
    routes.push(
      ...flattenedRoute({
        ...child,
        path: route.path + child.path,
        components: { ...components },
        mergedComponents: components,
        component: child.component || lastComponent
      })
    );
  }
  return routes;
};
var expandOptionalRoutes = (routes) => {
  const newRoutes = [];
  routes.forEach((route) => {
    expandOptionals(route.path).forEach((path) => {
      newRoutes.push({ ...route, path });
    });
  });
  return newRoutes;
};

// src/navigator.ts
import { reconcile as reconcile3 } from "solid-js/store";
var createNavigate = (routes, pathname, setPathname, setSearchAndHash) => {
  return (path, options) => {
    let newPath = path;
    let currentPathname = pathname();
    if (currentPathname.endsWith("/")) {
      currentPathname = currentPathname.slice(0, -1);
    }
    if (newPath.startsWith("./")) {
      newPath = currentPathname + "/" + newPath.slice(2);
    }
    if (newPath.startsWith("../")) {
      newPath = currentPathname + "/" + newPath;
    }
    if (options?.replace) {
      history.replaceState(options.state || null, "", newPath);
    } else {
      history.pushState(options?.state || null, "", newPath);
    }
    if (!isValidPath(routes, location.pathname)) {
    }
    batch2(() => {
      setPathname(location.pathname);
      setSearchAndHash(getSearchAndHash());
    });
  };
};
var useNavigate = () => {
  const context = useRouterContext();
  return context.navigate;
};
var Navigate = (props) => {
  const navigate = useNavigate();
  navigate(props.href, { replace: true });
  return null;
};
function useSearchParams() {
  const context = useRouterContext();
  const navigate = useNavigate();
  const updateQuery = (query, options) => {
    context.setQuery(reconcile3(query));
    const url = new URL(window.location.href);
    const newSearch = new URLSearchParams(context.query);
    url.search = newSearch.toString();
    navigate(url.pathname + url.search + url.hash, options);
  };
  return [
    context.location.query,
    updateQuery
  ];
}

// src/Outlet.tsx
import { createMemo as createMemo3 } from "solid-js";
var Fragment = () => <></>;
var Outlet = (props) => {
  const context = useRouterContext();
  const matched = () => context.matched();
  const getName = () => props.name || props.children;
  const component = createMemo3(() => {
    const name = getName();
    if (!name) {
      const rootComponent = matched()?.route.component;
      if (!rootComponent) {
        return Fragment;
      }
      return rootComponent;
    }
    const components = context.matched()?.route.components || context.matched()?.route.mergedComponents || {};
    const component2 = components[name];
    if (!component2) {
      return Fragment;
    }
    return component2;
  });
  const MemoComponent = createMemo3(() => {
    return component()();
  });
  return <>{MemoComponent}</>;
};

// src/Route.ts
import { children as children2, createMemo as createMemo4, mergeProps } from "solid-js";
var Route = (props) => {
  const childRoutes = children2(() => props.children).toArray;
  return mergeProps(props, {
    get children() {
      return childRoutes();
    }
  });
};
var matchComponent = (name) => {
  const context = useRouterContext();
  const matched = () => context.matched();
  const component = createMemo4(() => {
    const components = matched()?.route.components || matched()?.route.mergedComponents || {};
    return components[name()];
  });
  return createMemo4(() => {
    return component();
  });
};

// src/Link.tsx
import { createMemo as createMemo5, splitProps } from "solid-js";
var A = (props) => {
  const [, rest] = splitProps(props, ["href", "state"]);
  const location2 = useLocation();
  const resolvedHref = () => {
    if (!props.href)
      return props.href;
    let newPath = props.href;
    let currentPathname = location2.pathname;
    if (currentPathname.endsWith("/")) {
      currentPathname = currentPathname.slice(0, -1);
    }
    if (newPath.startsWith("./")) {
      newPath = currentPathname + "/" + newPath.slice(2);
    }
    if (newPath.startsWith("../")) {
      newPath = currentPathname + "/" + newPath;
    }
    return newPath;
  };
  return <a {...rest} href={resolvedHref()} sn-link d-state={JSON.stringify(props.state)} />;
};
var useMatch = (path) => {
  const location2 = useLocation();
  const matchers = createMemo5(() => {
    return expandOptionals(path()).map((path2) => createMatcher(path2));
  });
  return createMemo5(() => {
    for (let i = 0; i < matchers().length; i++) {
      const matcher = matchers()[i];
      const match = matcher(location2.pathname);
      if (match) {
        return match;
      }
    }
  });
};
export {
  A,
  Navigate,
  Outlet,
  Route,
  Router,
  matchComponent,
  useLocation,
  useMatch,
  useNavigate,
  useParams,
  useSearchParams
};
